/**
 * Query hooks for organization data
 */

import { useQuery } from '@tanstack/react-query';
import { apiEndpoints } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { useStore } from '@/store';
import type { Organization } from '@/store/types';

/**
 * Hook to fetch the current organization
 */
export const useOrganizationQuery = () => {
  const isAuthenticated = useStore((state) => state.isAuthenticated);
  const setCurrentOrganization = useStore((state) => state.setCurrentOrganization);

  return useQuery({
    queryKey: queryKeys.currentOrganization(),
    queryFn: async () => {
      const response = await apiEndpoints.organizations.getCurrent();

      // Update store with current organization
      if (response) {
        setCurrentOrganization(response as Organization);
      }

      return response as Organization;
    },
    enabled: isAuthenticated,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: (failureCount, error: any) => {
      // Don't retry if organization doesn't exist (needs setup)
      if (error?.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

/**
 * Hook to fetch workspaces for an organization
 */
export const useOrganizationWorkspacesQuery = (organizationId?: string) => {
  const isAuthenticated = useStore((state) => state.isAuthenticated);
  const currentOrg = useStore((state) => state.currentOrganization);
  const setWorkspaces = useStore((state) => state.setWorkspaces);

  const orgId = organizationId || currentOrg?.id;

  return useQuery({
    queryKey: queryKeys.workspacesByOrg(orgId!),
    queryFn: async () => {
      const response = await apiEndpoints.organizations.getWorkspaces(orgId!);

      // Update store with workspaces
      if (Array.isArray(response)) {
        setWorkspaces(response);
      }

      return response;
    },
    enabled: isAuthenticated && !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
};
